import { useMutation, useQueryClient } from '@tanstack/react-query';
import type { AxiosError } from 'axios';
import { useContext } from 'react';

import AssignedToTask from '@/components/AssignedToTask/AssignedToTask';
import SnoozedUntil from '@/components/shared/SnoozedUntil';
import TaskDueDate from '@/components/TaskDueDate';
import type { Props } from '@/components/TaskItem/types';
import TaskMenu from '@/components/TaskMenu';
import TaskStatus from '@/components/TaskStatus';
import { Color } from '@/constants/color';
import AppContext from '@/contexts/appContext';
import PersonContext from '@/pages/Person/PersonContext';
import WorkRepository from '@/repositories/WorkRepository';
import type { ItemPayload } from '@/types/item';
import Badge from '@/ui/components/Badge';
import Container from '@/ui/components/Container';
import DualTime from '@/ui/components/DualTime/DualTime';
import ListCard from '@/ui/components/ListCard';
import Popover from '@/ui/components/Popover';
import TagsEditor from '@/ui/components/TagsEditor/TagsEditor';
import formatStaffName from '@/utils/formatStaffName';
import itemStatusName from '@/utils/itemStatusName';
import mapAxiosErrorMessages from '@/utils/mapAxiosErrorMessages';
import statusColor from '@/utils/statusColor';

const TaskItem = ({ item }: Props) => {
  const { addMessage } = useContext(AppContext);
  const { person } = useContext(PersonContext);
  const workRepo = WorkRepository();
  const queryClient = useQueryClient();
  const { blueprint_tags } = item;
  const itemHref = item.person_id
    ? `/people/${item.person_id}/tasks/${item.id}`
    : `/tasks/${item.id}`;
  const updateItem = useMutation({
    mutationFn: async ({ id, values }: { id: string; values: Partial<ItemPayload> }) => {
      return workRepo.updateItem(id, values);
    },
    onSuccess: ({ data }) => {
      if (!data) return;

      queryClient.invalidateQueries({ queryKey: ['getPersonTasks', data.person_id] });
      queryClient.invalidateQueries({ queryKey: ['getTasks', data.taskable_id] });
      queryClient.invalidateQueries({ queryKey: ['getAllTasks'] });
    },
    onError: (error: AxiosError) => addMessage(mapAxiosErrorMessages(error))
  });

  return (
    <Container>
      <ListCard headingActions={<TaskMenu item={item} />} headingHref={itemHref} title={item.name}>
        <div className="relative flex flex-wrap py-1">
          <span className="m-1">
            <Popover
              buttonClass="rounded-full"
              buttonColor={statusColor[item.status]}
              buttonRound
              buttonSize="xs"
              popoverButtonText={
                <span className="flex items-center">{itemStatusName(item.status)}</span>
              }>
              <TaskStatus item={item} updateItem={updateItem} />
            </Popover>
          </span>

          <SnoozedUntil item={item} updateItem={updateItem} />

          {item.due_at && (
            <span className="m-1">
              <Popover
                buttonColor={Color.GRAY}
                buttonRound
                buttonSize="xs"
                popoverButtonText={
                  <>
                    <span className="mr-0.5">Due:</span>
                    <DualTime dateTime={new Date(item.due_at)} memberTZ={person?.time_zone} />
                  </>
                }>
                <TaskDueDate itemId={item.id} dueDate={item.due_at} updateItem={updateItem} />
              </Popover>
            </span>
          )}

          <AssignedToTask item={item} />
        </div>

        <hr className="mb-1" />

        <div className="flex items-center flex-wrap gap-1">
          <span className="text-xs leading-5 text-gray-500 mr-1">Tags</span>
          {blueprint_tags && !!blueprint_tags.length && (
            <div className="mr-1">
              {blueprint_tags.map((tag, index) => {
                return <Badge key={`tag-${index}`} color={Color.BLUE} text={tag.name} />;
              })}
            </div>
          )}

          <TagsEditor id={item.id} contentType="items" />
        </div>

        <div className="flex py-1">
          <p className="text-xs leading-5 text-gray-500">
            {item.status === 'closed' ? (
              <>
                Completed{' '}
                {item.last_completed_at && (
                  <DualTime
                    dateTime={new Date(item.last_completed_at)}
                    memberTZ={person?.time_zone}
                  />
                )}
                <span>
                  {item.last_completed_by && (
                    <> by {formatStaffName({ staff: item.last_completed_by })}</>
                  )}
                </span>
              </>
            ) : (
              <>
                Created{' '}
                <DualTime dateTime={new Date(item.created_at)} memberTZ={person?.time_zone} />
                <span>
                  {item.created_by && <> by {formatStaffName({ staff: item.created_by })}</>}
                </span>
              </>
            )}
          </p>
        </div>
      </ListCard>
    </Container>
  );
};

export default TaskItem;
